/**
 * Playable Learn letter-course game CONTENT extracted from Pixley iOS
 * (Pixley-iOS/Learn AlphabetA–E; F–Z merged from letterGameConfigsFZ.js). Do not invent assets or answers not
 * present in the Swift banks/views.
 *
 * Engines: soundSort | sentenceBuilder | letterHunt | catchFalling |
 * pictureGrid | bubblePop | guidedTrace | pictureMatch | storyPages |
 * discovery | callSequence | wordBuilder | strokeAssembly | memoryPair |
 * soundPairs | soundPosition | genericTap | sentenceMeaning
 */

/** @typedef {{ word: string, assetName: string, isTarget?: boolean, startingSound?: string }} PhonicsItem */
/** @typedef {{ x: number, y: number }} NormPoint */


import {
  LETTER_GAME_CONFIGS_FZ,
  LETTER_GAME_ORDER_FZ,
} from './letterGameConfigsFZ.js';

export const LETTER_GAME_CONFIGS = {
  A: {
    feedAlligator: {
      engine: 'soundSort',
      title: 'A or Not A?',
      prompt: 'Drag the picture where it belongs.',
      background: 'a_game_sort_orchard_bg',
      zones: {
        target: { title: 'Starts with A', letter: 'A' },
        other: { title: "Doesn't start with A", letter: '×' },
      },
      roundSize: 10,
      targetHalf: true,
      targetItems: [
        { word: 'apple', assetName: 'a_word_apple', isTarget: true },
        { word: 'ant', assetName: 'a_word_ant', isTarget: true },
        { word: 'alligator', assetName: 'a_word_alligator', isTarget: true },
        { word: 'astronaut', assetName: 'a_word_astronaut', isTarget: true },
        { word: 'ambulance', assetName: 'a_word_ambulance', isTarget: true },
        { word: 'anchor', assetName: 'a_word_anchor', isTarget: true },
        { word: 'acorn', assetName: 'a_word_acorn', isTarget: true },
        { word: 'airplane', assetName: 'a_word_airplane', isTarget: true },
        { word: 'avocado', assetName: 'a_word_avocado', isTarget: true },
      ],
      distractorItems: [
        { word: 'ball', assetName: 'a_word_ball', isTarget: false },
        { word: 'bus', assetName: 'a_word_bus', isTarget: false },
        { word: 'dog', assetName: 'a_word_dog', isTarget: false },
        { word: 'fish', assetName: 'a_word_fish', isTarget: false },
        { word: 'moon', assetName: 'a_word_moon', isTarget: false },
        { word: 'sock', assetName: 'a_word_sock', isTarget: false },
        { word: 'tree', assetName: 'a_word_tree', isTarget: false },
        { word: 'truck', assetName: 'a_word_truck', isTarget: false },
        { word: 'egg', assetName: 'a_word_egg', isTarget: false },
      ],
    },

    sentenceBuilder: {
      engine: 'sentenceBuilder',
      title: 'Build an A Sentence',
      prompt: 'Drag each word onto its matching space.',
      background: 'a_game_sentence_garden_bg',
      roundSize: 5,
      sentences: [
        { words: ['An', 'ant', 'walks.'], focusWord: 'ant', shuffleSeed: 11 },
        { words: ['I', 'see', 'an', 'apple.'], focusWord: 'apple', shuffleSeed: 12 },
        { words: ['The', 'alligator', 'naps.'], focusWord: 'alligator', shuffleSeed: 13 },
        { words: ['An', 'astronaut', 'waves.'], focusWord: 'astronaut', shuffleSeed: 14 },
        { words: ['The', 'ambulance', 'helps.'], focusWord: 'ambulance', shuffleSeed: 15 },
        { words: ['An', 'anchor', 'is', 'heavy.'], focusWord: 'anchor', shuffleSeed: 16 },
        { words: ['The', 'airplane', 'can', 'fly.'], focusWord: 'airplane', shuffleSeed: 17 },
        { words: ['An', 'avocado', 'rolls.'], focusWord: 'avocado', shuffleSeed: 18 },
        { words: ['The', 'apple', 'tastes', 'sweet.'], focusWord: 'apple', shuffleSeed: 19 },
        { words: ['A', 'tiny', 'ant', 'waves.'], focusWord: 'ant', shuffleSeed: 20 },
      ],
      focusAssets: {
        ant: 'a_word_ant',
        apple: 'a_word_apple',
        alligator: 'a_word_alligator',
        astronaut: 'a_word_astronaut',
        ambulance: 'a_word_ambulance',
        anchor: 'a_word_anchor',
        airplane: 'a_word_airplane',
        avocado: 'a_word_avocado',
      },
    },

    letterHunt: {
      engine: 'letterHunt',
      title: 'Letter Hunt',
      prompt: 'Tap every A and a. Leave the other letters.',
      background: 'a_game_letter_hunt_bg',
      targets: ['A', 'a'],
      targetCopies: 6,
      distractors: [
        'O', 'S', 'C', 'B', 'P', 'D', 'G', 'Q', 'U', 'J', 'V', 'M',
        'N', 'W', 'H', 'K', 'X', 'Y', 'Z', 'R', 'o', 'c', 'e', 's',
      ],
      distractorCount: 12,
      layoutPoints: [
        [0.12, 0.2], [0.31, 0.19], [0.53, 0.22], [0.78, 0.19], [0.9, 0.32],
        [0.15, 0.39], [0.4, 0.37], [0.66, 0.41], [0.84, 0.49], [0.1, 0.56],
        [0.31, 0.59], [0.54, 0.55], [0.72, 0.65], [0.91, 0.69], [0.16, 0.77],
        [0.38, 0.82], [0.62, 0.76], [0.82, 0.84],
      ],
    },

    appleTree: {
      engine: 'catchFalling',
      title: 'Apple Tree',
      prompt: 'Catch words that start with A. Let the other words fall.',
      background: 'a_game_apple_catch_bg',
      catchCount: 3,
      missCount: 3,
      targetItems: [
        { word: 'apple', assetName: 'a_word_apple', isTarget: true },
        { word: 'ant', assetName: 'a_word_ant', isTarget: true },
        { word: 'alligator', assetName: 'a_word_alligator', isTarget: true },
        { word: 'astronaut', assetName: 'a_word_astronaut', isTarget: true },
        { word: 'ambulance', assetName: 'a_word_ambulance', isTarget: true },
        { word: 'anchor', assetName: 'a_word_anchor', isTarget: true },
        { word: 'acorn', assetName: 'a_word_acorn', isTarget: true },
        { word: 'airplane', assetName: 'a_word_airplane', isTarget: true },
        { word: 'avocado', assetName: 'a_word_avocado', isTarget: true },
      ],
      distractorItems: [
        { word: 'ball', assetName: 'a_word_ball', isTarget: false },
        { word: 'bus', assetName: 'a_word_bus', isTarget: false },
        { word: 'dog', assetName: 'a_word_dog', isTarget: false },
        { word: 'fish', assetName: 'a_word_fish', isTarget: false },
        { word: 'moon', assetName: 'a_word_moon', isTarget: false },
        { word: 'sock', assetName: 'a_word_sock', isTarget: false },
        { word: 'tree', assetName: 'a_word_tree', isTarget: false },
        { word: 'truck', assetName: 'a_word_truck', isTarget: false },
        { word: 'egg', assetName: 'a_word_egg', isTarget: false },
      ],
    },

    pictureGrid: {
      engine: 'pictureGrid',
      title: 'Find the A Picture',
      prompt: 'Tap the picture named below.',
      background: 'a_game_picture_grid_bg',
      spokenPromptTemplate: 'Find {word}',
      items: [
        { word: 'apple', assetName: 'a_word_apple', isTarget: true },
        { word: 'ant', assetName: 'a_word_ant', isTarget: true },
        { word: 'alligator', assetName: 'a_word_alligator', isTarget: true },
        { word: 'astronaut', assetName: 'a_word_astronaut', isTarget: true },
        { word: 'ambulance', assetName: 'a_word_ambulance', isTarget: true },
        { word: 'anchor', assetName: 'a_word_anchor', isTarget: true },
        { word: 'acorn', assetName: 'a_word_acorn', isTarget: true },
        { word: 'airplane', assetName: 'a_word_airplane', isTarget: true },
        { word: 'avocado', assetName: 'a_word_avocado', isTarget: true },
      ],
    },

    popTheA: {
      engine: 'bubblePop',
      title: 'A Bubble Pop',
      prompt: 'Pop only pictures that start with A.',
      background: 'a_game_bubble_pond_bg',
      targetCount: 6,
      distractorCount: 3,
      targetItems: [
        { word: 'apple', assetName: 'a_word_apple', isTarget: true },
        { word: 'ant', assetName: 'a_word_ant', isTarget: true },
        { word: 'alligator', assetName: 'a_word_alligator', isTarget: true },
        { word: 'astronaut', assetName: 'a_word_astronaut', isTarget: true },
        { word: 'ambulance', assetName: 'a_word_ambulance', isTarget: true },
        { word: 'anchor', assetName: 'a_word_anchor', isTarget: true },
        { word: 'acorn', assetName: 'a_word_acorn', isTarget: true },
        { word: 'airplane', assetName: 'a_word_airplane', isTarget: true },
        { word: 'avocado', assetName: 'a_word_avocado', isTarget: true },
      ],
      distractorItems: [
        { word: 'ball', assetName: 'a_word_ball', isTarget: false },
        { word: 'bus', assetName: 'a_word_bus', isTarget: false },
        { word: 'dog', assetName: 'a_word_dog', isTarget: false },
        { word: 'fish', assetName: 'a_word_fish', isTarget: false },
        { word: 'moon', assetName: 'a_word_moon', isTarget: false },
        { word: 'sock', assetName: 'a_word_sock', isTarget: false },
        { word: 'tree', assetName: 'a_word_tree', isTarget: false },
        { word: 'truck', assetName: 'a_word_truck', isTarget: false },
        { word: 'egg', assetName: 'a_word_egg', isTarget: false },
      ],
    },

    traceA: {
      engine: 'guidedTrace',
      title: 'Trace A and a',
      prompt: 'Start at the glowing number and trace to the star.',
      background: 'a_game_trace_yard_bg',
      cases: {
        uppercase: {
          glyph: 'A',
          strokeCount: 3,
          prompts: ['Big line down.', 'Big line down.', 'Little line across.'],
          strokes: [
            [
              { x: 0.5, y: 0.12 },
              { x: 0.19, y: 0.88 },
            ],
            [
              { x: 0.5, y: 0.12 },
              { x: 0.81, y: 0.88 },
            ],
            [
              { x: 0.35, y: 0.58 },
              { x: 0.65, y: 0.58 },
            ],
          ],
        },
        lowercase: {
          glyph: 'a',
          strokeCount: 1,
          prompts: ['Keep your finger down: around, then down with a little tail.'],
          introSpeech:
            'Lowercase a. Keep your finger down: go around, then down with a little tail.',
          strokes: [
            [
              { x: 0.66, y: 0.34 },
              { x: 0.59, y: 0.25 },
              { x: 0.48, y: 0.21 },
              { x: 0.37, y: 0.23 },
              { x: 0.28, y: 0.31 },
              { x: 0.23, y: 0.43 },
              { x: 0.23, y: 0.57 },
              { x: 0.28, y: 0.7 },
              { x: 0.38, y: 0.78 },
              { x: 0.5, y: 0.8 },
              { x: 0.6, y: 0.74 },
              { x: 0.66, y: 0.62 },
              { x: 0.66, y: 0.48 },
              { x: 0.66, y: 0.34 },
              { x: 0.68, y: 0.5 },
              { x: 0.68, y: 0.66 },
              { x: 0.7, y: 0.78 },
              { x: 0.77, y: 0.81 },
            ],
          ],
        },
      },
    },
  },

  B: {
    storyPages: {
      engine: 'storyPages',
      title: 'Build the B Story',
      prompt: 'Drag the B words',
      background: null,
      notes:
        'Backdrop is a code-drawn sky/mint gradient (no image asset). Round picks 3 random pages from catalog. Page 2 omitted (no B word). Drag each B-word token into its blank in the sentence; B words = tokens whose alphabetic core lowercased starts with "b".',
      pagesPerRound: 3,
      pages: [
        {
          pageNumber: 1,
          text: 'Zippy, a playful orange tiger, smiled at the bright playground.',
          imageAssetName: 'b_story_page_1',
          bWords: ['bright'],
        },
        {
          pageNumber: 3,
          text: 'Brown stripes crossed his soft fur, and a breeze tickled his belly.',
          imageAssetName: 'b_story_page_3',
          bWords: ['Brown', 'breeze', 'belly'],
        },
        {
          pageNumber: 4,
          text: 'Chubby paws found a blue ball. Zippy bounced it very softly.',
          imageAssetName: 'b_story_page_4',
          // Chubby starts with C — not a B blank. B blanks: blue, ball, bounced.
          bWords: ['blue', 'ball', 'bounced'],
        },
        {
          pageNumber: 5,
          text: 'Beside the ball stood a big letter B. Zippy said, “B makes the buh sound.”',
          imageAssetName: 'b_story_page_5',
          bWords: ['Beside', 'ball', 'big', 'B', 'B', 'buh'],
        },
        {
          pageNumber: 6,
          text: '“Ball begins with B,” said Zippy. A blue bird sang from a branch.',
          imageAssetName: 'b_story_page_6',
          bWords: ['Ball', 'begins', 'B', 'blue', 'bird', 'branch'],
        },
        {
          pageNumber: 7,
          text: 'Zippy found a little book. “Books begin with B, too.”',
          imageAssetName: 'b_story_page_7',
          bWords: ['book', 'Books', 'begin', 'B'],
        },
        {
          pageNumber: 8,
          text: 'Zippy waved bye to letter B. His letter adventure made him smile.',
          imageAssetName: 'b_story_page_8',
          bWords: ['bye', 'B'],
        },
      ],
    },

    bOrNotB: {
      engine: 'soundSort',
      title: 'B or Not B?',
      prompt: 'Drag each picture to the side where it belongs.',
      background: 'b_game_sort_blueberry_meadow_bg',
      zones: {
        target: { title: 'Starts with B', letter: 'B' },
        other: { title: "Doesn't start with B", letter: '×' },
      },
      roundTargetCount: 5,
      roundDistractorCount: 5,
      targetItems: [
        { word: 'ball', assetName: 'a_word_ball', isTarget: true },
        { word: 'bus', assetName: 'a_word_bus', isTarget: true },
        { word: 'banana', assetName: 'b_word_whole_banana', isTarget: true },
        { word: 'bee', assetName: 'ch_bee', isTarget: true },
        { word: 'book', assetName: 'ch_book', isTarget: true },
        { word: 'balloon', assetName: 'ch_balloon_red', isTarget: true },
      ],
      distractorItems: [
        { word: 'dog', assetName: 'a_word_dog', isTarget: false },
        { word: 'fish', assetName: 'a_word_fish', isTarget: false },
        { word: 'moon', assetName: 'a_word_moon', isTarget: false },
        { word: 'sock', assetName: 'a_word_sock', isTarget: false },
        { word: 'tree', assetName: 'a_word_tree', isTarget: false },
        { word: 'egg', assetName: 'a_word_egg', isTarget: false },
      ],
    },

    letterHunt: {
      engine: 'letterHunt',
      title: 'Butterfly B Hunt',
      prompt: 'Find uppercase B and lowercase b.',
      background: 'b_game_sort_blueberry_meadow_bg',
      targets: ['B', 'b'],
      distractors: ['D', 'd', 'P', 'p', 'R', 'q'],
      notes:
        'Uses AlphabetBonusLetterHuntGameView: ~8 target tiles cycling B/b plus distractor fillers; positions are generated at runtime.',
    },

    pictureMatch: {
      engine: 'pictureMatch',
      title: 'Bluebird Picture Call',
      prompt: 'Listen, then tap the picture I name.',
      background: 'b_game_sort_blueberry_meadow_bg',
      items: [
        { word: 'ball', assetName: 'a_word_ball', isTarget: true },
        { word: 'bus', assetName: 'a_word_bus', isTarget: true },
        { word: 'banana', assetName: 'b_word_whole_banana', isTarget: true },
        { word: 'bee', assetName: 'ch_bee', isTarget: true },
        { word: 'book', assetName: 'ch_book', isTarget: true },
        { word: 'balloon', assetName: 'ch_balloon_red', isTarget: true },
      ],
    },

    soundGarden: {
      engine: 'soundSort',
      title: 'B Sound Garden',
      prompt: 'Drag the picture to its first-sound garden.',
      background: 'b_game_sort_blueberry_meadow_bg',
      items: [
        { word: 'ball', assetName: 'a_word_ball', isTarget: true },
        { word: 'bus', assetName: 'a_word_bus', isTarget: true },
        { word: 'banana', assetName: 'b_word_whole_banana', isTarget: true },
        { word: 'bee', assetName: 'ch_bee', isTarget: true },
        { word: 'dog', assetName: 'a_word_dog', isTarget: false },
        { word: 'fish', assetName: 'a_word_fish', isTarget: false },
        { word: 'moon', assetName: 'a_word_moon', isTarget: false },
        { word: 'sock', assetName: 'a_word_sock', isTarget: false },
      ],
    },

    wordBuilder: {
      engine: 'wordBuilder',
      title: 'Build B Words',
      prompt: 'Drag the first letter into the word.',
      background: 'b_game_sort_blueberry_meadow_bg',
      letter: 'B',
      endings: ['all', 'us', 'ee', 'ook'],
      choices: ['B', 'D', 'P'],
      builtWords: ['ball', 'bus', 'bee', 'book'],
    },

    traceB: {
      engine: 'guidedTrace',
      title: 'Trace B and b',
      prompt: 'Start at the glowing number and trace to the star.',
      background: 'b_game_trace_art_garden_bg',
      cases: {
        uppercase: {
          glyph: 'B',
          strokeCount: 3,
          prompts: ['Big line down.', 'Around the top.', 'Around the bottom.'],
          strokes: [
            [
              { x: 0.3, y: 0.12 },
              { x: 0.3, y: 0.88 },
            ],
            [
              { x: 0.3, y: 0.12 },
              { x: 0.56, y: 0.12 },
              { x: 0.73, y: 0.2 },
              { x: 0.76, y: 0.32 },
              { x: 0.68, y: 0.44 },
              { x: 0.3, y: 0.5 },
            ],
            [
              { x: 0.3, y: 0.5 },
              { x: 0.62, y: 0.49 },
              { x: 0.78, y: 0.6 },
              { x: 0.78, y: 0.73 },
              { x: 0.68, y: 0.85 },
              { x: 0.3, y: 0.88 },
            ],
          ],
        },
        lowercase: {
          glyph: 'b',
          strokeCount: 2,
          prompts: ['Big line down.', 'Around the belly.'],
          introSpeech: 'Lowercase b. Big line down, then around the belly.',
          strokes: [
            [
              { x: 0.32, y: 0.12 },
              { x: 0.32, y: 0.88 },
            ],
            [
              { x: 0.32, y: 0.43 },
              { x: 0.51, y: 0.39 },
              { x: 0.68, y: 0.46 },
              { x: 0.75, y: 0.59 },
              { x: 0.73, y: 0.73 },
              { x: 0.62, y: 0.84 },
              { x: 0.47, y: 0.86 },
              { x: 0.35, y: 0.78 },
              { x: 0.32, y: 0.6 },
              { x: 0.32, y: 0.43 },
            ],
          ],
        },
      },
    },
  },

  C: {
    archaeologyDig: {
      engine: 'discovery',
      title: 'Canyon C Discovery',
      prompt: 'Tap each sandy spot to discover a C word.',
      background: 'c_archaeology_dig_bg',
      items: [
        { title: 'Curled fossil', word: 'Coral', assetName: 'c_fossil_coral' },
        { title: 'Clam fossil', word: 'Clam', assetName: 'c_fossil_clam' },
        { title: 'Crab fossil', word: 'Crab', assetName: 'c_fossil_crab' },
      ],
      revealTemplate: 'C is for {word}!',
    },

    sentenceBuilder: {
      engine: 'sentenceBuilder',
      title: 'Build a C Sentence',
      prompt: 'Listen, then tap each word in order.',
      background: 'c_sentence_storybook_garden_bg',
      interaction: 'tapInOrder',
      sentences: [
        {
          words: ['A', 'cat', 'curls.'],
          focus: { word: 'cat', assetName: 'c_word_cat' },
          shuffleSeed: 31,
        },
        {
          words: ['The', 'car', 'can', 'go.'],
          focus: { word: 'car', assetName: 'c_word_car' },
          shuffleSeed: 32,
        },
        {
          words: ['A', 'cloud', 'can', 'float.'],
          focus: { word: 'cloud', assetName: 'c_word_cloud' },
          shuffleSeed: 33,
        },
        {
          words: ['The', 'cake', 'has', 'candles.'],
          focus: { word: 'cake', assetName: 'c_word_cake' },
          shuffleSeed: 34,
        },
        {
          words: ['I', 'can', 'see', 'a', 'crown.'],
          focus: { word: 'crown', assetName: 'c_word_crown' },
          shuffleSeed: 35,
        },
      ],
    },

    letterHunt: {
      engine: 'letterHunt',
      title: 'Canyon C Hunt',
      prompt: 'Find uppercase C and lowercase c.',
      background: 'c_archaeology_dig_bg',
      targets: ['C', 'c'],
      distractors: ['G', 'g', 'O', 'o', 'Q', 'e'],
      notes: 'AlphabetBonusLetterHuntGameView layout (runtime positions).',
    },

    pictureMatch: {
      engine: 'pictureMatch',
      title: 'Cove Picture Call',
      prompt: 'Listen, then tap the picture I name.',
      background: 'c_sentence_storybook_garden_bg',
      items: [
        { word: 'cat', assetName: 'c_word_cat_cutout', isTarget: true },
        { word: 'car', assetName: 'c_word_car_cutout', isTarget: true },
        { word: 'cloud', assetName: 'c_word_cloud_cutout', isTarget: true },
        { word: 'cake', assetName: 'c_word_cake_cutout', isTarget: true },
        { word: 'crown', assetName: 'c_word_crown_cutout', isTarget: true },
        { word: 'cookie', assetName: 'c_word_cookie', isTarget: true },
        { word: 'cow', assetName: 'c_word_cow', isTarget: true },
        { word: 'carrot', assetName: 'c_word_carrot', isTarget: true },
        { word: 'corn', assetName: 'c_word_corn', isTarget: true },
      ],
    },

    soundSort: {
      engine: 'soundSort',
      title: 'C Sound Sort',
      prompt: 'Drag the picture to its first-sound garden.',
      background: 'c_archaeology_dig_bg',
      items: [
        { word: 'cat', assetName: 'c_word_cat_cutout', isTarget: true },
        { word: 'car', assetName: 'c_word_car_cutout', isTarget: true },
        { word: 'cloud', assetName: 'c_word_cloud_cutout', isTarget: true },
        { word: 'cake', assetName: 'c_word_cake_cutout', isTarget: true },
        { word: 'crown', assetName: 'c_word_crown_cutout', isTarget: true },
        { word: 'dog', assetName: 'a_word_dog', isTarget: false },
        { word: 'fish', assetName: 'a_word_fish', isTarget: false },
        { word: 'moon', assetName: 'a_word_moon', isTarget: false },
        { word: 'sock', assetName: 'a_word_sock', isTarget: false },
      ],
    },

    caveEcho: {
      engine: 'wordBuilder',
      title: 'Build C Words',
      prompt: 'Drag the first letter into the word.',
      background: 'c_trace_creative_garden_bg',
      letter: 'C',
      endings: ['at', 'ar', 'ake', 'up'],
      choices: ['C', 'G', 'T'],
      builtWords: ['cat', 'car', 'cake', 'cup'],
    },

    traceC: {
      engine: 'guidedTrace',
      title: 'Trace C and c',
      prompt: 'Start at the glowing number and trace to the star.',
      background: 'c_trace_creative_garden_bg',
      cases: {
        uppercase: {
          glyph: 'C',
          strokeCount: 1,
          prompts: ['Start at top. Curve back and around.'],
          strokes: [
            [
              { x: 0.76, y: 0.21 },
              { x: 0.67, y: 0.13 },
              { x: 0.55, y: 0.1 },
              { x: 0.42, y: 0.12 },
              { x: 0.31, y: 0.2 },
              { x: 0.23, y: 0.32 },
              { x: 0.19, y: 0.47 },
              { x: 0.2, y: 0.62 },
              { x: 0.26, y: 0.76 },
              { x: 0.36, y: 0.86 },
              { x: 0.49, y: 0.9 },
              { x: 0.63, y: 0.88 },
              { x: 0.75, y: 0.79 },
            ],
          ],
        },
        lowercase: {
          glyph: 'c',
          strokeCount: 1,
          prompts: ['Small curve back and around.'],
          strokes: [
            [
              { x: 0.7, y: 0.35 },
              { x: 0.62, y: 0.28 },
              { x: 0.51, y: 0.25 },
              { x: 0.39, y: 0.27 },
              { x: 0.3, y: 0.35 },
              { x: 0.26, y: 0.47 },
              { x: 0.27, y: 0.6 },
              { x: 0.33, y: 0.7 },
              { x: 0.43, y: 0.76 },
              { x: 0.55, y: 0.77 },
              { x: 0.66, y: 0.72 },
              { x: 0.72, y: 0.66 },
            ],
          ],
        },
      },
    },
  },

  D: {
    doorDiscovery: {
      engine: 'discovery',
      title: 'Daisy Door Discovery',
      prompt: 'Tap each door to meet a word that starts with D.',
      background: 'd_game_daisy_doors_bg',
      items: [
        { word: 'dog', assetName: 'd_word_dog', isTarget: true },
        { word: 'duck', assetName: 'd_word_duck', isTarget: true },
        { word: 'drum', assetName: 'd_word_drum', isTarget: true },
        { word: 'door', assetName: 'd_word_door', isTarget: true },
        { word: 'daisy', assetName: 'd_word_daisy', isTarget: true },
        { word: 'dolphin', assetName: 'd_word_dolphin', isTarget: true },
      ],
      intro:
        'Welcome to letter D. Tap each garden door to discover a word that starts with D.',
    },

    soundSort: {
      engine: 'soundSort',
      title: 'D or Not D?',
      prompt: 'Drag each picture to the Starts with D side or the Not D side.',
      background: 'd_game_duck_pond_sort_bg',
      zones: {
        target: { title: 'Starts with D' },
        other: { title: 'Not D' },
      },
      roundTargetCount: 5,
      roundDistractorCount: 5,
      targetItems: [
        { word: 'dog', assetName: 'd_word_dog', isTarget: true },
        { word: 'duck', assetName: 'd_word_duck', isTarget: true },
        { word: 'drum', assetName: 'd_word_drum', isTarget: true },
        { word: 'door', assetName: 'd_word_door', isTarget: true },
        { word: 'daisy', assetName: 'd_word_daisy', isTarget: true },
        { word: 'dolphin', assetName: 'd_word_dolphin', isTarget: true },
        { word: 'deer', assetName: 'd_word_deer', isTarget: true },
        { word: 'desk', assetName: 'd_word_desk', isTarget: true },
        { word: 'donut', assetName: 'd_word_donut', isTarget: true },
        { word: 'dinosaur', assetName: 'd_word_dinosaur', isTarget: true },
      ],
      distractorItems: [
        { word: 'apple', assetName: 'a_word_apple', isTarget: false },
        { word: 'ball', assetName: 'a_word_ball', isTarget: false },
        { word: 'bus', assetName: 'a_word_bus', isTarget: false },
        { word: 'egg', assetName: 'a_word_egg', isTarget: false },
        { word: 'fish', assetName: 'a_word_fish', isTarget: false },
        { word: 'moon', assetName: 'a_word_moon', isTarget: false },
        { word: 'sock', assetName: 'a_word_sock', isTarget: false },
        { word: 'tree', assetName: 'a_word_tree', isTarget: false },
      ],
    },

    letterHunt: {
      engine: 'letterHunt',
      title: 'Dancing D Hunt',
      prompt: 'Tap every uppercase D and lowercase d.',
      background: 'd_game_dance_hunt_bg',
      targets: ['D', 'd'],
      targetCopies: 6,
      distractors: [
        'O', 'B', 'P', 'C', 'G', 'Q', 'R', 'S', 'U', 'M', 'N', 'A',
        'b', 'p', 'o', 'c', 'g', 'q', 'a', 'e',
      ],
      distractorCount: 12,
      layoutPoints: [
        [0.12, 0.22], [0.32, 0.2], [0.53, 0.24], [0.78, 0.2], [0.9, 0.33],
        [0.15, 0.4], [0.4, 0.38], [0.66, 0.42], [0.84, 0.5], [0.1, 0.57],
        [0.31, 0.6], [0.54, 0.56], [0.72, 0.66], [0.91, 0.7], [0.16, 0.78],
        [0.38, 0.83], [0.62, 0.77], [0.82, 0.85],
      ],
    },

    pictureMatch: {
      engine: 'pictureMatch',
      title: 'Dolphin Dock Match',
      prompt: 'Listen carefully, then tap the picture I name.',
      background: 'd_game_dolphin_dock_match_bg',
      spokenPromptTemplate: 'Find the {word}.',
      items: [
        { word: 'dog', assetName: 'd_word_dog', isTarget: true },
        { word: 'duck', assetName: 'd_word_duck', isTarget: true },
        { word: 'drum', assetName: 'd_word_drum', isTarget: true },
        { word: 'door', assetName: 'd_word_door', isTarget: true },
        { word: 'daisy', assetName: 'd_word_daisy', isTarget: true },
        { word: 'dolphin', assetName: 'd_word_dolphin', isTarget: true },
        { word: 'deer', assetName: 'd_word_deer', isTarget: true },
        { word: 'desk', assetName: 'd_word_desk', isTarget: true },
        { word: 'donut', assetName: 'd_word_donut', isTarget: true },
      ],
    },

    drumParade: {
      engine: 'soundSort',
      title: 'Drum the D Words',
      prompt: 'Listen to one word. Play the D drum, or let it stay quiet.',
      background: 'd_game_drum_parade_bg',
      notes:
        'Not a drag-sort: shows one pictured word at a time; tap "D DRUM" if it begins with D, or "QUIET" if another sound. Round = 6 targets + 3 distractors shuffled.',
      choices: [
        { id: 'dDrum', label: 'D DRUM', subtitle: 'Begins with D', meansTarget: true },
        { id: 'quiet', label: 'QUIET', subtitle: 'Another sound', meansTarget: false },
      ],
      roundTargetCount: 6,
      roundDistractorCount: 3,
      targetItems: [
        { word: 'dog', assetName: 'd_word_dog', isTarget: true },
        { word: 'duck', assetName: 'd_word_duck', isTarget: true },
        { word: 'drum', assetName: 'd_word_drum', isTarget: true },
        { word: 'door', assetName: 'd_word_door', isTarget: true },
        { word: 'daisy', assetName: 'd_word_daisy', isTarget: true },
        { word: 'dolphin', assetName: 'd_word_dolphin', isTarget: true },
        { word: 'deer', assetName: 'd_word_deer', isTarget: true },
        { word: 'desk', assetName: 'd_word_desk', isTarget: true },
        { word: 'donut', assetName: 'd_word_donut', isTarget: true },
        { word: 'dinosaur', assetName: 'd_word_dinosaur', isTarget: true },
      ],
      distractorItems: [
        { word: 'apple', assetName: 'a_word_apple', isTarget: false },
        { word: 'ball', assetName: 'a_word_ball', isTarget: false },
        { word: 'bus', assetName: 'a_word_bus', isTarget: false },
        { word: 'egg', assetName: 'a_word_egg', isTarget: false },
        { word: 'fish', assetName: 'a_word_fish', isTarget: false },
        { word: 'moon', assetName: 'a_word_moon', isTarget: false },
        { word: 'sock', assetName: 'a_word_sock', isTarget: false },
        { word: 'tree', assetName: 'a_word_tree', isTarget: false },
      ],
    },

    sentenceBuilder: {
      engine: 'sentenceBuilder',
      title: 'Build a D Sentence',
      prompt: 'Listen, then tap each word in order.',
      background: 'd_game_sentence_garden_bg',
      interaction: 'tapInOrder',
      sentences: [
        { words: ['Dogs', 'dig.'], focusWord: 'dog', shuffleSeed: 41 },
        { words: ['A', 'duck', 'dives.'], focusWord: 'duck', shuffleSeed: 42 },
        { words: ['The', 'daisy', 'grows.'], focusWord: 'daisy', shuffleSeed: 43 },
        { words: ['The', 'drum', 'is', 'loud.'], focusWord: 'drum', shuffleSeed: 44 },
        {
          words: ['The', 'dolphin', 'can', 'dance.'],
          focusWord: 'dolphin',
          shuffleSeed: 45,
        },
      ],
      focusAssets: {
        dog: 'd_word_dog',
        duck: 'd_word_duck',
        daisy: 'd_word_daisy',
        drum: 'd_word_drum',
        dolphin: 'd_word_dolphin',
      },
    },

    traceD: {
      engine: 'guidedTrace',
      title: 'Trace D and d',
      prompt: 'Trace uppercase D and lowercase d. Follow each glowing path from its number.',
      background: 'd_game_drawing_den_trace_bg',
      cases: {
        uppercase: {
          glyph: 'D',
          strokeCount: 2,
          prompts: ['Big line down.', 'Around from the top.'],
          strokes: [
            [
              { x: 0.3, y: 0.12 },
              { x: 0.3, y: 0.88 },
            ],
            [
              { x: 0.3, y: 0.12 },
              { x: 0.53, y: 0.12 },
              { x: 0.7, y: 0.21 },
              { x: 0.79, y: 0.36 },
              { x: 0.79, y: 0.62 },
              { x: 0.7, y: 0.78 },
              { x: 0.53, y: 0.88 },
              { x: 0.3, y: 0.88 },
            ],
          ],
        },
        lowercase: {
          glyph: 'd',
          strokeCount: 1,
          prompts: [
            'Keep your finger down: around the circle, then the tall line up.',
          ],
          strokes: [
            [
              { x: 0.65, y: 0.43 },
              { x: 0.54, y: 0.36 },
              { x: 0.4, y: 0.38 },
              { x: 0.3, y: 0.48 },
              { x: 0.28, y: 0.62 },
              { x: 0.35, y: 0.74 },
              { x: 0.49, y: 0.8 },
              { x: 0.6, y: 0.76 },
              { x: 0.65, y: 0.69 },
              { x: 0.65, y: 0.55 },
              { x: 0.65, y: 0.43 },
              { x: 0.66, y: 0.29 },
              { x: 0.66, y: 0.14 },
            ],
          ],
        },
      },
    },
  },

  E: {
    eggDiscovery: {
      engine: 'discovery',
      title: 'Egg Garden Discovery',
      prompt: 'Tap each egg to meet a word that starts with E.',
      background: 'e_game_egg_orchard_discovery_bg',
      items: [
        { word: 'egg', assetName: 'e_word_egg', startingSound: 'shortE' },
        { word: 'elephant', assetName: 'e_word_elephant', startingSound: 'shortE' },
        { word: 'elbow', assetName: 'e_word_elbow', startingSound: 'shortE' },
        { word: 'evergreen', assetName: 'e_word_evergreen', startingSound: 'shortE' },
        { word: 'engine', assetName: 'e_word_engine', startingSound: 'shortE' },
        { word: 'elk', assetName: 'e_word_elk', startingSound: 'shortE' },
      ],
      intro:
        'Welcome to letter E. Tap each colorful egg to discover a word that starts with E.',
    },

    shortESort: {
      engine: 'soundSort',
      title: 'Starts with E?',
      prompt: 'Drag the picture to Starts with E or Not E.',
      background: 'e_game_short_e_sort_glade_bg',
      notes:
        'Despite the Swift name shortESort, correct zone is beginsWithLetterE (shortE + longE + otherE). Only otherLetter goes to Not E. Round: 5 from (shortE+otherE) + 4 otherStarting.',
      zones: {
        target: { title: 'Starts with E', subtitle: 'Starts with E' },
        other: { title: 'Not E' },
      },
      targetCriterion: 'beginsWithLetterE',
      shortEWords: [
        { word: 'egg', assetName: 'e_word_egg', startingSound: 'shortE' },
        { word: 'elephant', assetName: 'e_word_elephant', startingSound: 'shortE' },
        { word: 'elbow', assetName: 'e_word_elbow', startingSound: 'shortE' },
        { word: 'evergreen', assetName: 'e_word_evergreen', startingSound: 'shortE' },
        { word: 'engine', assetName: 'e_word_engine', startingSound: 'shortE' },
        { word: 'elk', assetName: 'e_word_elk', startingSound: 'shortE' },
        { word: 'elevator', assetName: 'e_word_elevator', startingSound: 'shortE' },
      ],
      otherEWords: [
        { word: 'eagle', assetName: 'e_word_eagle', startingSound: 'longE' },
        { word: 'eel', assetName: 'e_word_eel', startingSound: 'longE' },
        { word: 'ear', assetName: 'e_word_ear', startingSound: 'otherE' },
      ],
      otherStartingWords: [
        { word: 'apple', assetName: 'a_word_apple', startingSound: 'otherLetter' },
        { word: 'ball', assetName: 'a_word_ball', startingSound: 'otherLetter' },
        { word: 'dog', assetName: 'a_word_dog', startingSound: 'otherLetter' },
        { word: 'fish', assetName: 'a_word_fish', startingSound: 'otherLetter' },
        { word: 'tree', assetName: 'a_word_tree', startingSound: 'otherLetter' },
      ],
    },

    letterHunt: {
      engine: 'letterHunt',
      title: 'Evergreen E Hunt',
      prompt: 'Tap every uppercase E and lowercase e.',
      background: 'e_game_e_hunt_firefly_forest_bg',
      targets: ['E', 'e'],
      targetCopies: 6,
      distractors: [
        'F', 'B', 'P', 'C', 'G', 'L', 'R', 'S', 'U', 'M', 'N', 'A',
        'f', 'b', 'p', 'o', 'c', 'a', 'd', 'g',
      ],
      distractorCount: 12,
      layoutPoints: [
        [0.12, 0.22], [0.32, 0.2], [0.53, 0.24], [0.78, 0.2], [0.9, 0.33],
        [0.15, 0.4], [0.4, 0.38], [0.66, 0.42], [0.84, 0.5], [0.1, 0.57],
        [0.31, 0.6], [0.54, 0.56], [0.72, 0.66], [0.91, 0.7], [0.16, 0.78],
        [0.38, 0.83], [0.62, 0.77], [0.82, 0.85],
      ],
    },

    pictureMatch: {
      engine: 'pictureMatch',
      title: 'Echo Cove Match',
      prompt: 'Listen, then tap the picture I name.',
      background: 'e_game_echo_picture_match_bg',
      spokenPromptTemplate: 'Find the {word}.',
      items: [
        { word: 'egg', assetName: 'e_word_egg', startingSound: 'shortE' },
        { word: 'elephant', assetName: 'e_word_elephant', startingSound: 'shortE' },
        { word: 'elbow', assetName: 'e_word_elbow', startingSound: 'shortE' },
        { word: 'evergreen', assetName: 'e_word_evergreen', startingSound: 'shortE' },
        { word: 'engine', assetName: 'e_word_engine', startingSound: 'shortE' },
        { word: 'elk', assetName: 'e_word_elk', startingSound: 'shortE' },
        { word: 'elevator', assetName: 'e_word_elevator', startingSound: 'shortE' },
        { word: 'eagle', assetName: 'e_word_eagle', startingSound: 'longE' },
        { word: 'eel', assetName: 'e_word_eel', startingSound: 'longE' },
      ],
    },

    soundFerry: {
      engine: 'pictureMatch',
      title: 'E Word Ferry',
      prompt: 'Listen, then tap the matching E picture.',
      background: 'e_game_e_sound_ferry_bg',
      notes:
        'Each turn shows 3 choices: current target plus two other ferry items (offset by +1 and +3 in the round list, order rotates). Round order is fixed indices [egg, elephant, engine, eagle, eel, ear].',
      items: [
        { word: 'egg', assetName: 'e_word_egg', startingSound: 'shortE' },
        { word: 'elephant', assetName: 'e_word_elephant', startingSound: 'shortE' },
        { word: 'engine', assetName: 'e_word_engine', startingSound: 'shortE' },
        { word: 'eagle', assetName: 'e_word_eagle', startingSound: 'longE' },
        { word: 'eel', assetName: 'e_word_eel', startingSound: 'longE' },
        { word: 'ear', assetName: 'e_word_ear', startingSound: 'otherE' },
      ],
      choicesPerTurn: 3,
    },

    wordWorkshop: {
      engine: 'wordBuilder',
      title: 'Engineer an E Word',
      prompt: 'Tap the letter blocks in order to build each word.',
      background: 'e_game_word_workshop_bg',
      puzzles: [
        {
          word: 'egg',
          assetName: 'e_word_egg',
          letters: ['E', 'G', 'G'],
          soundNote: 'Egg starts with E.',
          shuffleSeed: 11,
        },
        {
          word: 'eel',
          assetName: 'e_word_eel',
          letters: ['E', 'E', 'L'],
          soundNote: 'Eel starts with E.',
          shuffleSeed: 37,
        },
        {
          word: 'elk',
          assetName: 'e_word_elk',
          letters: ['E', 'L', 'K'],
          soundNote: 'Elk starts with E.',
          shuffleSeed: 23,
        },
      ],
    },

    traceE: {
      engine: 'guidedTrace',
      title: 'Trace E and e',
      prompt: 'Trace uppercase E and lowercase e. Start at each glowing number and follow the path.',
      background: 'e_game_trace_art_studio_bg',
      cases: {
        uppercase: {
          glyph: 'E',
          strokeCount: 4,
          prompts: [
            'Big line down.',
            'Top arm across.',
            'Middle arm across.',
            'Bottom arm across.',
          ],
          strokes: [
            [
              { x: 0.32, y: 0.12 },
              { x: 0.32, y: 0.88 },
            ],
            [
              { x: 0.32, y: 0.12 },
              { x: 0.74, y: 0.12 },
            ],
            [
              { x: 0.32, y: 0.5 },
              { x: 0.65, y: 0.5 },
            ],
            [
              { x: 0.32, y: 0.88 },
              { x: 0.74, y: 0.88 },
            ],
          ],
        },
        lowercase: {
          glyph: 'e',
          strokeCount: 1,
          prompts: ['Around the top, then across the middle.'],
          strokes: [
            [
              { x: 0.32, y: 0.55 },
              { x: 0.66, y: 0.55 },
              { x: 0.65, y: 0.44 },
              { x: 0.58, y: 0.35 },
              { x: 0.47, y: 0.32 },
              { x: 0.36, y: 0.36 },
              { x: 0.29, y: 0.46 },
              { x: 0.28, y: 0.6 },
              { x: 0.33, y: 0.72 },
              { x: 0.43, y: 0.79 },
              { x: 0.56, y: 0.79 },
              { x: 0.66, y: 0.73 },
            ],
          ],
        },
      },
    },
  },
};

/** Ordered mini-game ids matching iOS CaseIterable order. */
export const LETTER_GAME_ORDER = {
  A: [
    'feedAlligator',
    'sentenceBuilder',
    'letterHunt',
    'appleTree',
    'pictureGrid',
    'popTheA',
    'traceA',
  ],
  B: [
    'storyPages',
    'bOrNotB',
    'letterHunt',
    'pictureMatch',
    'soundGarden',
    'wordBuilder',
    'traceB',
  ],
  C: [
    'archaeologyDig',
    'sentenceBuilder',
    'letterHunt',
    'pictureMatch',
    'soundSort',
    'caveEcho',
    'traceC',
  ],
  D: [
    'doorDiscovery',
    'soundSort',
    'letterHunt',
    'pictureMatch',
    'drumParade',
    'sentenceBuilder',
    'traceD',
  ],
  E: [
    'eggDiscovery',
    'shortESort',
    'letterHunt',
    'pictureMatch',
    'soundFerry',
    'wordWorkshop',
    'traceE',
  ],
};

Object.assign(LETTER_GAME_CONFIGS, LETTER_GAME_CONFIGS_FZ);
Object.assign(LETTER_GAME_ORDER, LETTER_GAME_ORDER_FZ);

export default LETTER_GAME_CONFIGS;
